import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Music2, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { trackSocialClick, trackFooterSubscribe } from '@/utils/analytics';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, { email });
      toast.success("You're on the list!");
      trackFooterSubscribe();
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail === 'Already subscribed') {
        toast.info("You're already subscribed!");
      } else {
        toast.error('Something went wrong. Try again!');
      }
    }
    setLoading(false);
  };

  const quickLinks = [
    { name: 'Find a Crawl', href: '/crawls' },
    { name: 'About Us', href: '/about' },
    { name: 'Group Bookings', href: '/groups' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com' },
    { name: 'TikTok', icon: Music2, href: 'https://tiktok.com' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com' },
  ];

  return (
    <footer className="bg-[#080010]" data-testid="footer">
      <div className="w-full h-[5px] bg-hot-pink" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo & Tagline */}
          <div>
            <Link to="/" className="inline-block">
              <img
                src="https://customer-assets.emergentagent.com/job_8d9bacaf-3cbd-4601-a4fa-6b92074da917/artifacts/hvbdh30e_Logo_1.PNG"
                alt="Crawl Baby Crawl"
                className="w-[240px] h-auto"
                data-testid="footer-logo-img"
              />
            </Link>
            <p className="font-body text-warm-white/50 mt-4 text-sm leading-relaxed max-w-sm">
              The best bar crawl experiences in America. Join thousands of crawlers
              who've partied their way through the country's best nightlife.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-subheading font-semibold text-warm-white uppercase tracking-widest text-sm mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-warm-white/50 hover:text-hot-pink transition-colors font-body text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Email */}
          <div>
            <h4 className="font-subheading font-semibold text-warm-white uppercase tracking-widest text-sm mb-6">
              Follow Us
            </h4>
            <div className="flex gap-3 mb-8">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                  className="w-11 h-11 bg-card-surface flex items-center justify-center text-warm-white/60 hover:bg-hot-pink hover:text-white transition-all"
                  onClick={() => trackSocialClick(social.name)}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>

            <form onSubmit={handleSubscribe} className="flex">
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-email-input"
                className="flex-1 bg-card-surface border border-card-border px-4 py-3 text-warm-white placeholder:text-warm-white/40 font-body text-sm focus:outline-none focus:border-hot-pink"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-subscribe-btn"
                className="btn-primary px-5 py-3 text-sm"
              >
                {loading ? '...' : 'GO'}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-card-border mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-warm-white/30 text-xs font-body">
            &copy; {new Date().getFullYear()} Crawl Baby Crawl. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs">
            <Link to="/privacy-policy" className="text-warm-white/30 hover:text-warm-white transition-colors font-body">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-warm-white/30 hover:text-warm-white transition-colors font-body">
              Terms of Service
            </Link>
            <span className="text-hot-pink font-semibold font-body">21+ ONLY</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
